/**
 * Owner setting: how the interview evaluator is invoked for the WHOLE application (platform_settings
 * 'evaluation_mode', GET/PUT /admin/evaluation-mode). isolated = one model call per competency (default,
 * measured stable); single = one call for the whole set (cheaper/faster, measured to let competencies
 * influence each other when the criteria change). Every evaluation records the mode it ran in.
 */
import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import { Alert } from "../../components/icons.js";
import { apiErrorText } from "./adminUi.js";

type Mode = "isolated" | "single";
interface ModeState {
  mode: Mode;
  source: "owner_setting" | "env" | "default";
  updatedAt: string | null;
  options: Record<Mode, { calls: string; note: string }>;
}
const QK = ["admin-evaluation-mode"];
const SOURCE: Record<ModeState["source"], string> = {
  owner_setting: "set here by the owner",
  env: "from the EVALUATION_MODE environment variable (no owner setting yet)",
  default: "the default (no owner setting, no environment variable)",
};

export function EvaluationModeCard({ isOwner }: { isOwner: boolean }): ReactNode {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: QK, queryFn: () => api.get<ModeState>("/admin/evaluation-mode") });
  const [pick, setPick] = useState<Mode | null>(null);
  const [reason, setReason] = useState("");
  const save = useMutation({
    mutationFn: (mode: Mode) => api.put<ModeState>("/admin/evaluation-mode", { mode, reason }),
    onSuccess: () => {
      setPick(null);
      setReason("");
      void qc.invalidateQueries({ queryKey: QK });
    },
  });
  const d = q.data;
  if (!d) return null;
  const chosen = pick ?? d.mode;
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-h">
        <h3>Interview evaluation mode</h3>
        <span className="tag">applies to every evaluation · audited · reaches the worker within ~30 s</span>
      </div>
      <div className="card-b">
        <div className="sm mut" style={{ marginBottom: 8 }}>
          Now: <b>{d.mode}</b> — {SOURCE[d.source]}
          {d.updatedAt ? ` · ${new Date(d.updatedAt).toLocaleString()}` : ""}
        </div>
        {(["isolated", "single"] as Mode[]).map((m) => (
          <label key={m} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8, cursor: isOwner ? "pointer" : "default" }}>
            <input type="radio" name="evalmode" disabled={!isOwner} checked={chosen === m} onChange={() => setPick(m)} />
            <span>
              <b>{m === "isolated" ? "Isolated (recommended)" : "Single request"}</b> — {d.options[m].calls}
              <div className="sm mut">{d.options[m].note}</div>
            </span>
          </label>
        ))}
        {chosen === "single" && (
          <div className="banner warn" style={{ margin: "8px 0" }}>
            <Alert />
            <div>Single-request mode can change a competency's result when another competency is added or removed, so rankings may shift without any new evidence. Use it for cost experiments, not for decisions you rely on.</div>
          </div>
        )}
        {isOwner && pick && pick !== d.mode && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
            <input className="inp" style={{ minWidth: 280 }} placeholder="Why? (audited, min 4 characters)" value={reason} onChange={(e) => setReason(e.target.value)} />
            <button type="button" className="btn" disabled={reason.trim().length < 4 || save.isPending} onClick={() => save.mutate(pick)}>
              {save.isPending ? "Saving…" : `Switch to ${pick}`}
            </button>
            <button type="button" className="btn ghost" onClick={() => setPick(null)}>Cancel</button>
          </div>
        )}
        {save.isError && <div className="sm" style={{ color: "var(--crit)", marginTop: 6 }}>{apiErrorText(save.error, "Couldn't save the setting.")}</div>}
      </div>
    </div>
  );
}
